import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import Cookies from "js-cookie";
import Header from "../Header/Header";
import html2pdf from "html2pdf.js";
import { useRef } from "react";
import { FiDownload } from "react-icons/fi";

const OrderDetails = () => {
	const invoiceRef = useRef();

	const navigate = useNavigate();
	const { orderItemId } = useParams();
	const [order, setOrder] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState("");

	const token = Cookies.get("ecom_user_token");

	const formatDate = (date) =>
		new Date(date).toLocaleDateString("en-IN", {
			day: "2-digit",
			month: "short",
			year: "numeric",
		});

	const formatINR = (amount) =>
		new Intl.NumberFormat("en-IN", {
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
		}).format(amount);

	const generateInvoice = () => {
		if (!invoiceRef.current) return;

		const options = {
			margin: 10,
			filename: `Invoice_${order._id.slice(-8)}.pdf`,
			image: { type: "jpeg", quality: 0.98 },
			html2canvas: {
				scale: 2,
				useCORS: true,
			},
			jsPDF: {
				unit: "mm",
				format: "a4",
				orientation: "portrait",
			},
		};

		html2pdf()
			.set(options)
			.from(invoiceRef.current)
			.save();
	};

	useEffect(() => {
		const fetchOrder = async () => {
			try {
				setLoading(true);
				const { data } = await axios.get(
					`${process.env.REACT_APP_BACKEND_URL}/api/order/${orderItemId}`,
					{ headers: { Authorization: `Bearer ${token}` } }
				);
				setOrder(data.order);
			} catch (err) {
				setError("Failed to load order details");
			} finally {
				setLoading(false);
			}
		};
		if (orderItemId && token) fetchOrder();
	}, [orderItemId, token]);

	if (loading) return <div className="min-h-screen bg-[#fdfaf6] flex items-center justify-center italic text-[#5d4037]">Crafting your details...</div>;
	if (error) return <div className="p-10 text-center text-red-800 bg-[#fdfaf6]">{error}</div>;
	if (!order) return null;

	return (
		<>
			<Header />
			<div className="min-h-screen bg-[#fdfaf6] text-[#3e2723] font-serif p-4 sm:p-6 lg:p-10">
				<div className="max-w-4xl mx-auto">
					<div className="flex flex-col md:flex-row md:justify-between md:items-center">
						<button
							onClick={() => navigate(-1)}
							className="mb-6 flex items-center gap-2 text-[#8d6e63] hover:text-[#5d4037] transition-colors duration-300"
						>
							<span className="text-xl">«</span>
							<span className="uppercase tracking-[0.2em] text-[10px] font-bold">Return</span>
						</button>

						<button
							onClick={generateInvoice}
							className="group flex items-center gap-2 mb-2 md:mb-0 px-4 py-2 text-xs uppercase tracking-widest font-bold border border-[#8d6e63] text-[#5d4037] hover:bg-[#f5f0e8] transition-colors"
						>
							<FiDownload className="text-sm group-hover:translate-y-0.5 transition-transform" />
							<span>Download Invoice</span>
						</button>
					</div>

					{/* Main Parchment Card */}
					<div ref={invoiceRef} className="bg-white shadow-xl border-t-8 border-[#5d4037] p-5 sm:p-8 lg:p-12 relative overflow-hidden">
						{/* Decorative Corner (Hidden on very small screens to save space) */}
						<div className="hidden sm:block absolute top-0 right-0 w-16 h-16 bg-[#fdfaf6] rotate-45 translate-x-10 -translate-y-10 border-b border-stone-200"></div>

						{/* Header */}
						<header className="border-b border-stone-100 pb-6 mb-8 flex flex-col sm:flex-row justify-between items-start sm:items-baseline gap-4">
							<div>
								<h1 className="text-2xl sm:text-3xl font-light italic tracking-tight">Order Manifest</h1>
								<p className="text-[#8d6e63] text-xs mt-1 uppercase tracking-widest font-sans">ID: {order._id.slice(-8)}</p>
							</div>
							<div className="w-full sm:w-auto text-left sm:text-right">
								<span className="inline-block px-3 py-1 bg-[#f5f0e8] border border-[#d4a373] text-[#5d4037] text-[10px] font-bold uppercase tracking-widest">
									{order.status}
								</span>
							</div>
						</header>

						<div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10 text-xs uppercase tracking-widest font-bold text-[#8d6e63]">
							<div>
								<p className="text-[10px]">Order Date</p>
								<p className="text-[#5d4037] font-serif text-sm not-italic">
									{formatDate(order.createdAt)}
								</p>
							</div>

							<div>
								<p className="text-[10px]">Items</p>
								<p className="text-[#5d4037] font-serif text-sm not-italic">
									{order.sellers.reduce(
										(sum, s) => sum + s.products.length,
										0
									)}{" "}
									Items
								</p>
							</div>
						</div>

						{/* Info Grid */}
						<div className="grid grid-cols-1 sm:grid-cols-2 gap-8 mb-12">
							<section>
								<h3 className="uppercase text-[10px] font-bold tracking-[0.2em] text-[#8d6e63] mb-3">Destination</h3>
								<div className="text-base sm:text-lg leading-relaxed italic opacity-90">
									<p className="font-bold not-italic text-[#5d4037] mb-1">{order.shippingAddress.fullName}</p>
									<p className="text-[#5d4037]/80">{order.shippingAddress.addressLine1}</p>
									<p className="text-[#5d4037]/80">{order.shippingAddress.city}, {order.shippingAddress.state} {order.shippingAddress.zipCode}</p>
									<p className="text-xs pt-2 font-sans not-italic">Phone: {order.shippingAddress.phone}</p>
								</div>
							</section>

							<section className="sm:text-right">
								<h3 className="uppercase text-[10px] font-bold tracking-[0.2em] text-[#8d6e63] mb-3">Provenance</h3>
								<p className="text-base sm:text-lg italic">{order.payment.method === "cod"
									? "Cash on Delivery"
									: order.payment.method === "razorpay"
										? "Online Payment" : ""}
								</p>
								<p className="text-[10px] text-[#8d6e63] mt-1 italic uppercase tracking-widest font-bold">{order.payment.status}</p>
								{order.payment.method === "cod" && (
									<p className="text-[10px] mt-2 text-[#a1887f] italic">
										Pay on delivery
									</p>
								)}
							</section>
						</div>

						{/* Items List */}
						<div className="space-y-10">
							{order.sellers.map((seller) => (
								<div key={seller._id}>
									<h3 className="flex items-center gap-3 text-[#5d4037] text-xs font-bold mb-6 uppercase tracking-[0.15em]">
										<span className="italic normal-case font-serif text-base font-medium">From {seller.sellerId.businessName}</span>
										<span className="h-[1px] flex-1 bg-stone-100"></span>
									</h3>

									<div className="mb-6 flex flex-wrap gap-4 text-[10px] uppercase tracking-widest font-bold text-[#8d6e63]">
										{/* <span className="px-2 py-1 border bg-[#fdfaf6]">
											Seller Status: {seller.sellerStatus}
										</span> */}

										<span className="px-2 py-1 border bg-[#fdfaf6]">
											Shipping: {seller.tracking.status.replace("_", " ")}
										</span>

										{seller.tracking.carrier && (
											<span className="px-2 py-1 border bg-[#fdfaf6]">
												Carrier: {seller.tracking.carrier}
											</span>
										)}
									</div>

									<div className="space-y-8">
										{seller.products.map((item) => {
											const variant = item.productId.variants.find(v => v._id === item.variantId);
											return (
												<>
													<div key={item._id} className="flex flex-col md:flex-row gap-4 sm:gap-6 border-b border-stone-50 pb-8 last:border-0">
														{/* Product Image */}
														<div className="relative mx-auto md:mx-0 w-full max-w-[160px] md:w-32">
															<img
																src={variant?.images?.[0]}
																alt={item.productId.name}
																className="w-full aspect-[16/9] object-cover grayscale-[20%] rounded-sm shadow-sm"
															/>
															<div className="absolute inset-0 border border-black/5 pointer-events-none"></div>
														</div>

														{/* Details */}
														<div className="flex-1 space-y-2 text-center md:text-left">
															<h4 className="text-lg sm:text-xl font-medium text-[#3e2723] leading-tight">
																{item.productId.name}
															</h4>
															<p className="text-[10px] text-[#8d6e63] tracking-widest uppercase font-sans">SKU: {variant?.sku}</p>

															<div className="flex justify-center md:justify-start gap-6 text-sm pt-2 italic">
																<p><span className="text-[#8d6e63] not-italic text-[10px] uppercase tracking-tighter mr-1">Qty:</span> {item.quantity}</p>
																<p><span className="text-[#8d6e63] not-italic text-[10px] uppercase tracking-tighter mr-1">Rate:</span> ₹{formatINR(item.price)}</p>
															</div>
															<p className="text-[10px] mt-3 inline-block px-2 py-0.5 bg-[#fdfaf6] text-[#8d6e63] border border-stone-100 italic uppercase tracking-widest">
																{item.status}
															</p>
														</div>

														{/* Total per Item */}
														<div className="text-center md:text-right flex flex-col justify-center md:justify-end border-t border-stone-50 md:border-0 pt-4 md:pt-0">
															<p className="text-xl sm:text-2xl font-light text-[#5d4037]">₹{formatINR(item.total)}</p>
														</div>
													</div>

													{item.isReturnAvailable === false && (
														<p className="text-[10px] italic text-[#a1887f]">
															Return not available for this item
														</p>
													)}
												</>
											);
										})}
									</div>
								</div>
							))}
						</div>

						{/* Summary Footer */}
						<footer className="mt-12 pt-8 border-t-2 border-stone-100 bg-[#fcf9f3] -mx-5 sm:-mx-8 lg:-mx-12 px-5 sm:px-8 lg:px-12 pb-8">
							<div className="max-w-xs ml-auto space-y-3 text-sm italic">
								<div className="flex justify-between text-[#8d6e63]">
									<span>Subtotal</span>
									<span className="font-sans not-italic">
										₹{formatINR(order?.totalAmount - (order?.shippingCost + order?.taxes + order?.codCharge))}
									</span>
								</div>

								<div className="flex justify-between text-[#8d6e63]">
									<span>Shipping</span>
									<span className="font-sans not-italic">₹{formatINR(order.shippingCost)}</span>
								</div>

								{order.codCharge > 0 && (
									<div className="flex justify-between text-[#8d6e63]">
										<span>COD Charges</span>
										<span className="font-sans not-italic">₹{formatINR(order.codCharge)}</span>
									</div>
								)}

								<div className="flex justify-between border-b border-stone-200 pb-3 text-[#8d6e63]">
									<span>Taxes</span>
									<span className="font-sans not-italic">₹{formatINR(order.taxes)}</span>
								</div>

								<div className="flex justify-between text-xl sm:text-2xl not-italic font-bold text-[#3e2723] pt-2 font-serif">
									<span>Total</span>
									<span>₹{formatINR(order?.totalAmount)}</span>
								</div>

								{order.refundedAmount > 0 && (
									<div className="flex justify-between text-red-800 pt-2 border-t border-red-100 text-xs">
										<span>Refunded</span>
										<span>-₹{formatINR(order.refundedAmount)}</span>
									</div>
								)}
							</div>
						</footer>
					</div>

					<p className="text-center mt-8 text-[#8d6e63] text-[10px] tracking-[0.3em] uppercase opacity-60 px-4">
						Hand-packed with care &bull; Artisan Quality
					</p>
				</div>
			</div>
		</>
	);
};

export default OrderDetails;